import { useEffect, useMemo, useState } from "react"

import { Alert, Button, Card, Checkbox, Layout, Spin, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useLocation } from "react-router"

import LoginHeader from "../Components/LoginHeader"
import { request } from "../Services/Api"

const { Title, Paragraph } = Typography

// OAuth params forwarded verbatim between the GET (consent info) and POST (approve) calls.
const OAUTH_PARAM_KEYS = [
  "response_type",
  "client_id",
  "redirect_uri",
  "scope",
  "state",
  "code_challenge",
  "code_challenge_method"
]

// Path is relative to REACT_APP_BASE_API_ROUTE (which already includes /api).
const CONSENT_PATH = "/auth/mcp_oauth_authorize/"

const MCPConsentPage = () => {
  const { search, pathname } = useLocation()
  const { t } = useTranslation()
  const accessToken = useSelector((state) => state.auth?.accessToken)

  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [info, setInfo] = useState(null)
  const [selectedScopes, setSelectedScopes] = useState([])
  const [error, setError] = useState(null)
  const [denied, setDenied] = useState(false)

  const params = useMemo(() => {
    const qs = new URLSearchParams(search)
    const collected = {}

    OAUTH_PARAM_KEYS.forEach((key) => {
      const value = qs.get(key)
      if (value) collected[key] = value
    })

    return collected
  }, [search])

  useEffect(() => {
    document.title = "Connect Chargeback MCP"

    // Bounce to login and return to this exact page afterwards. The login/sign-up sagas read
    // `OAuthRedirect` and do a plain navigation back (no auto-approve).
    const bounceToLogin = () => {
      window.localStorage.setItem("OAuthRedirect", window.location.origin + pathname + search)
      window.location.href = "/login"
    }

    // Not logged in → straight to login.
    if (!accessToken) {
      bounceToLogin()
      return undefined
    }

    let cancelled = false

    ;(async () => {
      const response = await request.api.get(CONSENT_PATH, params)

      if (cancelled) return

      if (response.ok) {
        setInfo(response.data)
        setSelectedScopes((response.data.scopes || []).map((item) => item.code))
      } else if (response.status === 401) {
        // Token is present but stale/expired → re-authenticate, then come back here.
        bounceToLogin()
        return
      } else if (response.status === 429) {
        let message = t("Too many requests. Please try again later.")
        if (typeof response.data?.message?.detail === "string") {
          message = response.data.message.detail
        }
        setError(message)
        return
      } else {
        let message = t("Invalid authorization request.")
        if (typeof response.data?.message === "string") {
          message = response.data.message
        } else if (typeof response.data?.detail === "string") {
          message = response.data.detail
        }
        setError(message)
      }

      setLoading(false)
    })()

    return () => {
      cancelled = true
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [accessToken])

  const toggleScope = (code) => {
    setSelectedScopes((prev) =>
      prev.includes(code) ? prev.filter((item) => item !== code) : [...prev, code]
    )
  }

  const handleAllow = async () => {
    if (!selectedScopes.length) return

    setSubmitting(true)

    const scope = selectedScopes.join(" ")
    const response = await request.api.post(CONSENT_PATH, { ...params, scope, allow: true })

    // The authorize response carries the redirect_uri even on the OAuth 302 status, so act on
    // its presence rather than response.ok.
    const redirectUri = response.data?.redirect_uri
    if (redirectUri) {
      window.location.href = redirectUri
      return
    }

    setError(
      response.data?.message || response.data?.detail || t("Could not complete authorization.")
    )
    setSubmitting(false)
  }

  const handleDeny = () => {
    setDenied(true)
  }

  const renderBody = () => {
    if (error) {
      return <Alert type="error" message={error} showIcon />
    }

    if (denied) {
      return (
        <Alert type="info" message={t("You denied access. You can close this window.")} showIcon />
      )
    }

    if (!info) return null

    // Consent copy depends on what the client is asking for: a write scope means the assistant
    // can, after explicit confirmation, change data (e.g. resolve an alert) — not just read it.
    const hasWriteScope = (info.scopes || []).some((item) => item.code?.endsWith(":write"))

    return (
      <Card style={{ maxWidth: 440, margin: "0 auto" }}>
        <Title level={4}>
          {info.client_name} {t("wants to connect to your Chargeback account")}
        </Title>

        <Paragraph type="secondary">{t("Choose what it can access:")}</Paragraph>

        <div style={{ display: "flex", flexDirection: "column", gap: 10, margin: "8px 0" }}>
          {(info.scopes || []).map((item) => (
            <Checkbox
              key={item.code}
              checked={selectedScopes.includes(item.code)}
              onChange={() => toggleScope(item.code)}
            >
              {item.label}
            </Checkbox>
          ))}
        </div>

        <Paragraph type="secondary" style={{ marginTop: 12 }}>
          {t(
            hasWriteScope
              ? "This connection can read your data and, only with your confirmation, update alert " +
                  "statuses. It can be revoked at any time. Never share passwords or API keys in the AI " +
                  "chat. The assistant already has the access it needs."
              : "This connection is read-only and can be revoked at any time. Never share passwords " +
                  "or API keys in the AI chat. The assistant already has the access it needs."
          )}
        </Paragraph>

        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          <Button
            type="primary"
            block
            loading={submitting}
            disabled={selectedScopes.length === 0}
            onClick={handleAllow}
          >
            {t("Allow")}
          </Button>
          <Button block disabled={submitting} onClick={handleDeny}>
            {t("Deny")}
          </Button>
        </div>
      </Card>
    )
  }

  return (
    <Layout>
      <div className="login-row">
        <LoginHeader />

        <Spin spinning={loading || submitting}>
          <div className="login-content">
            <div className="page-content">{renderBody()}</div>
          </div>
        </Spin>
      </div>
    </Layout>
  )
}

export default MCPConsentPage
